import { Link } from "react-router-dom";
import { Instagram, Twitter, ChevronUp, Gamepad2, Youtube, Mail } from "lucide-react";

const Footer = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="relative overflow-hidden border-t border-[hsl(var(--arcade-violet)/0.25)] bg-[hsl(var(--surface))]">
      {/* Background flourishes */}
      <div className="pointer-events-none absolute inset-0 grid-bg opacity-20" />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 20% 100%, hsl(var(--arcade-violet) / 0.25), transparent 60%), radial-gradient(ellipse 40% 30% at 90% 0%, hsl(var(--arcade-cyan) / 0.15), transparent 60%)",
        }}
      />
      <div className="pointer-events-none absolute inset-0 noise" />

      {/* Back to top */}
      <button
        onClick={scrollToTop}
        className="relative z-[1] flex w-full items-center justify-center gap-2 border-b border-[hsl(var(--arcade-violet)/0.2)] py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground transition-colors hover:bg-[hsl(var(--arcade-violet)/0.1)] hover:text-[hsl(var(--arcade-cyan))]"
      >
        <ChevronUp className="h-4 w-4" /> // back to top
      </button>

      <div className="container relative z-[1] mx-auto px-4 py-16">
        {/* Big brand mark */}
        <div className="mb-12 flex flex-col items-start justify-between gap-8 border-b border-[hsl(var(--arcade-violet)/0.2)] pb-12 md:flex-row md:items-end">
          <div>
            <Link to="/" className="flex items-center gap-3">
              <span className="relative flex h-12 w-12 items-center justify-center rounded-lg border border-[hsl(var(--arcade-cyan)/0.4)] bg-gradient-to-br from-[hsl(var(--arcade-violet)/0.3)] to-[hsl(var(--arcade-cyan)/0.15)] shadow-[0_0_25px_hsl(var(--arcade-violet)/0.4)]">
                <Gamepad2 className="h-6 w-6 text-[hsl(var(--arcade-cyan))]" />
              </span>
              <span className="font-display text-4xl font-black tracking-tight text-arcade-gradient sm:text-5xl">
                GROVEFAN
              </span>
            </Link>
            <p className="mt-4 max-w-md font-body text-sm text-muted-foreground">
              India's gamer-first marketplace. Peripherals, audio, setup gear & merch — built different, priced for the culture.
            </p>
            <div className="mt-6 flex gap-2">
              {[
                { href: "https://www.instagram.com/grovefanreal?igsh=ZWJ6bW4wdWFyd2hx", Icon: Instagram, label: "Instagram" },
                { href: "https://x.com/grovefanreal", Icon: Twitter, label: "Twitter" },
                { href: "https://www.youtube.com/@kavvaie", Icon: Youtube, label: "YouTube" },
                { href: "mailto:0722parth@gmail.com", Icon: Mail, label: "Email" },
              ].map(({ href, Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="group relative flex h-10 w-10 items-center justify-center rounded-full border border-[hsl(var(--arcade-violet)/0.35)] bg-[hsl(var(--card)/0.5)] text-muted-foreground backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-[hsl(var(--arcade-cyan)/0.7)] hover:text-[hsl(var(--arcade-cyan))] hover:shadow-[0_0_20px_hsl(var(--arcade-cyan)/0.4)]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Live status pill */}
          <div className="chip-neon violet">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--arcade-cyan))] opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--arcade-cyan))]" />
            </span>
            Store online · 24/7
          </div>
        </div>

        {/* Columns */}
        <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-4">
          {[
            {
              title: "Shop",
              links: [
                { to: "/shop", label: "All Gear" },
                { to: "/shop", label: "New Drops" },
                { to: "/shop", label: "Best Sellers" },
                { to: "/shop?sort=price-low", label: "Today's Deals" },
                { to: "/brands", label: "Brands" },
              ],
            },
            {
              title: "Departments",
              links: [
                { to: "/shop?cat=Peripherals", label: "Peripherals" },
                { to: "/shop?cat=Audio", label: "Audio" },
                { to: "/shop?cat=Setup+Gear", label: "Setup Gear" },
                { to: "/shop?cat=Gaming+Merch", label: "Gaming Merch" },
                { to: "/shop?cat=Collectibles", label: "Collectibles" },
                { to: "/shop?cat=Accessories", label: "Accessories" },
              ],
            },
            {
              title: "Company",
              links: [
                { to: "/about", label: "About Us" },
                { to: "/contact", label: "Contact" },
                { to: "/blog", label: "Blog" },
                { to: "/sell", label: "Start Selling", accent: true },
                { to: "/affiliate", label: "Affiliate Program", accent: true },
              ],
            },
            {
              title: "Support",
              links: [
                { to: "/shipping-returns", label: "Shipping & Returns" },
                { to: "/faq", label: "FAQ" },
                { to: "/gift-cards", label: "Gift Cards" },
                { to: "/gift-cards/check-balance", label: "Check GC Balance" },
                { to: "/size-guide", label: "Size Guide" },
                { to: "/privacy", label: "Privacy Policy" },
                { to: "/terms", label: "Terms of Service" },
              ],
            },
          ].map((col) => (
            <div key={col.title}>
              <h3 className="eyebrow-mono mb-4">// {col.title}</h3>
              <div className="flex flex-col gap-2">
                {col.links.map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    className={`group inline-flex w-fit items-center gap-1.5 text-sm transition-colors ${
                      (l as { accent?: boolean }).accent
                        ? "font-semibold text-[hsl(var(--arcade-cyan))] hover:brightness-125"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="opacity-0 transition-opacity group-hover:opacity-100">▸</span>
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-[hsl(var(--arcade-violet)/0.2)] pt-6 font-mono text-[11px] uppercase tracking-widest text-muted-foreground sm:flex-row">
          <span>© 2026 GROVEFAN.SYS // ALL RIGHTS RESERVED</span>
          <span className="text-[hsl(var(--arcade-cyan))]">Built for the GenZ gamer.</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
